// Today board. Segmented control switches between three bands so the
// page isn't an endless scroll. Each band is a short grouped-list of
// sections with tone-colored leading icons.

import { useState } from "react";
import { KPI } from "./components";
import { todayBoard } from "./fixtures";
import { Chevron, MockShell, Row } from "./MockShell";

type Card = {
  id: string;
  title: string;
  subtitle?: string;
  tone: "info" | "warn" | "danger" | "ok";
  meta?: string;
  action?: string;
};

const toneIcon: Record<Card["tone"], { bg: string; glyph: string }> = {
  danger: { bg: "bg-rose-500", glyph: "!" },
  warn: { bg: "bg-amber-500", glyph: "!" },
  info: { bg: "bg-sky-500", glyph: "i" },
  ok: { bg: "bg-emerald-500", glyph: "✓" },
};

function LeadingIcon({ tone }: { tone: Card["tone"] }) {
  const t = toneIcon[tone];
  return (
    <span
      className={`h-7 w-7 rounded-lg ${t.bg} text-white grid place-items-center text-[13px] font-semibold shrink-0`}
      aria-hidden="true"
    >
      {t.glyph}
    </span>
  );
}

function Section({ header, cards }: { header: string; cards: readonly Card[] }) {
  if (cards.length === 0) return null;
  return (
    <section className="mb-5">
      <div className="flex items-baseline justify-between px-1 pb-2">
        <h2 className="text-[17px] font-semibold tracking-tight text-slate-900">{header}</h2>
        <span className="text-[13px] text-slate-500 tabular-nums">{cards.length}</span>
      </div>
      <div className="bg-white rounded-2xl overflow-hidden">
        {cards.map((c) => (
          <Row
            key={c.id}
            leading={<LeadingIcon tone={c.tone} />}
            title={c.title}
            subtitle={c.subtitle}
            trailing={<Chevron className="text-slate-300" />}
            mock={`Would open: ${c.title}`}
          />
        ))}
      </div>
    </section>
  );
}

type BandId = "attention" | "active" | "scheduled";

const kpis = [
  { label: "Sales", value: "$8,214" },
  { label: "Firearms", value: "6" },
  { label: "Drawers", value: "2" },
  { label: "Approvals", value: "1" },
];

type SectionDef = { header: string; cards: readonly Card[] };

const needsAttention: SectionDef[] = [
  { header: "4473s in progress", cards: todayBoard.openForm4473s },
  { header: "NICS delayed", cards: todayBoard.nicsDelayed },
  { header: "Minnesota holds", cards: todayBoard.mnReportHolds },
  { header: "Compliance", cards: todayBoard.compliance },
];

const inProgress: SectionDef[] = [
  { header: "Transfers awaiting pickup", cards: todayBoard.transfers },
  { header: "Gunsmithing", cards: todayBoard.gunsmithing },
  { header: "Layaways", cards: todayBoard.layaways },
  { header: "NFA", cards: todayBoard.nfa },
];

const scheduled: SectionDef[] = [
  { header: "Special orders", cards: todayBoard.specialOrders },
  { header: "Web orders", cards: todayBoard.webOrders },
  { header: "Classes", cards: todayBoard.classes },
];

function countBand(sections: SectionDef[]): number {
  return sections.reduce((n, s) => n + s.cards.length, 0);
}

export function TodayBoard() {
  const [band, setBand] = useState<BandId>("attention");
  const bands: { id: BandId; label: string; sections: SectionDef[] }[] = [
    { id: "attention", label: "Attention", sections: needsAttention },
    { id: "active", label: "Active", sections: inProgress },
    { id: "scheduled", label: "Scheduled", sections: scheduled },
  ];
  const current = bands.find((b) => b.id === band) ?? bands[0];
  if (!current) return null;

  return (
    <MockShell title="Today" signedInAs="Elliott R." device="Counter 1">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 pb-10">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-6">
          {kpis.map((k) => (
            <KPI key={k.label} {...k} />
          ))}
        </div>

        <div className="inline-flex gap-1 p-1 rounded-full bg-slate-200/70 w-full mb-5">
          {bands.map((b) => {
            const count = countBand(b.sections);
            const active = b.id === band;
            return (
              <button
                key={b.id}
                type="button"
                onClick={() => setBand(b.id)}
                className={`flex-1 min-h-9 rounded-full text-[14px] font-medium transition inline-flex items-center justify-center gap-1.5 ${
                  active ? "bg-white text-slate-900 shadow-sm" : "text-slate-600"
                }`}
              >
                <span>{b.label}</span>
                <span
                  className={`text-[12px] tabular-nums ${active ? "text-slate-500" : "text-slate-400"}`}
                >
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        {current.sections.map((s) => (
          <Section key={s.header} header={s.header} cards={s.cards} />
        ))}
      </div>
    </MockShell>
  );
}
